import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import { useMediaQuery, useTheme } from '@mui/material';
import { BsSearch as SearchIcon } from 'react-icons/bs';
import { IoClose as ClearIcon, IoTrash as ClearFiltersIcon } from 'react-icons/io5';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import TicketExportJob from '@IssueConsoleRepresentationalDisplay/TicketExportJob';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import FieldService from '@Helpers/URLHelpers/FieldService';
import FormUserAccessLinkService from '@Helpers/URLHelpers/KaiziqResolve/views/FormUserAccessLinkService';
import TicketUserEmailCountViewService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketUserEmailCountViewService';
import ErrorHandler from '@Root/ErrorHandler';
import './Filters.scss';

function SearchableMultiFilterControl({
  className,
  label,
  options,
  selectedOptions,
  onSelectionChange,
  getOptionLabel,
  getOptionValue,
  isLoading,
  renderLoader,
  noOptionsLabel
}) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [overflowAnchorEl, setOverflowAnchorEl] = useState(null);
  const minimumSearchLength = 3;

  const hiddenOptions = selectedOptions.slice(1);
  const visibleOption = selectedOptions[0];

  const removeOption = (optionToRemove) => {
    const next = selectedOptions.filter((option) => getOptionValue(option) !== getOptionValue(optionToRemove));
    onSelectionChange(next);
  };

  const filterOptions = (allOptions, state) => {
    const query = state.inputValue.trim();
    const selectedValues = new Set(selectedOptions.map((o) => getOptionValue(o)));
    if (query.length >= minimumSearchLength) {
      const normalized = query.toLowerCase();
      const matching = allOptions.filter((option) => getOptionLabel(option).toLowerCase().includes(normalized));
      return [
        ...matching.filter((o) => selectedValues.has(getOptionValue(o))),
        ...matching.filter((o) => !selectedValues.has(getOptionValue(o)))
      ];
    }
    return [
      ...allOptions.filter((o) => selectedValues.has(getOptionValue(o))),
      ...allOptions.filter((o) => !selectedValues.has(getOptionValue(o)))
    ];
  };

  return (
    <Box className={className + '__advancedControl ' + className + '__advancedControl--searchable'}>
      {isLoading && renderLoader()}
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={selectedOptions}
        onChange={(_, selected) => onSelectionChange(selected)}
        inputValue={inputValue}
        onInputChange={(_, nextInput) => setInputValue(nextInput)}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
        renderTags={() => null}
        noOptionsText={`No ${noOptionsLabel} found`}
        className={className + '__advancedAutocomplete'}
        slotProps={{
          paper: {
            className: className + '__menuPaper'
          }
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox size="small" checked={selected} />
            <ListItemText primary={getOptionLabel(option)} />
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search or click ▾ for full list"
            size="small"
          />
        )}
      />

      <Box className={className + '__pillRow'}>
        {selectedOptions.length === 0 ? (
          <Chip label="All" size="small" className={className + '__pill ' + className + '__pill--all'} />
        ) : (
          <>
            <Chip
              label={getOptionLabel(visibleOption)}
              size="small"
              className={className + '__pill'}
              onDelete={() => removeOption(visibleOption)}
            />
            {hiddenOptions.length > 0 && (
              <Chip
                label={`+${hiddenOptions.length}`}
                size="small"
                onClick={(event) => setOverflowAnchorEl(event.currentTarget)}
                className={className + '__pill ' + className + '__pill--more'}
              />
            )}
          </>
        )}
      </Box>

      <Popover
        open={Boolean(overflowAnchorEl)}
        anchorEl={overflowAnchorEl}
        onClose={() => setOverflowAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: className + '__pillPopover' }}
      >
        <Box className={className + '__pillPopover__content'}>
          {hiddenOptions.map((option) => (
            <Chip
              key={String(getOptionValue(option))}
              label={getOptionLabel(option)}
              size="small"
              className={className + '__pill'}
              onDelete={() => removeOption(option)}
            />
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

export default function Filters(props) {
  const {
    filters,
    onFilterChange,
    handleSearch,
    clearFilters,
    defaultFilters,
    statusList,
    isTicketFilter
  } = props;

  const errorHandler = useMemo(() => new ErrorHandler(), []);
  const fieldService = useMemo(() => new FieldService(), []);
  const formUserAccessLinkService = useMemo(() => new FormUserAccessLinkService(), []);
  const ticketUserEmailCountViewService = useMemo(() => new TicketUserEmailCountViewService(), []);
  const { currentProjectId, getSimpleUsersByProjectId } = useReduxProject();
  const getSimpleUsersByProjectIdRef = useRef(getSimpleUsersByProjectId);
  const lastLoadedFilterOptionsKeyRef = useRef('');
  const isFilterOptionsRequestInFlightRef = useRef(false);
  const theme = useTheme();
  const isPhoneView = useMediaQuery(theme.breakpoints.down('sm'));
  const className = 'filters';
  const initialQuery = filters?.ticketFilter?.searchQuery || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [isDirty, setIsDirty] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [createdByEmailOptions, setCreatedByEmailOptions] = useState([]);
  const [dateRangeMode, setDateRangeMode] = useState('');

  const ticketFilter = filters?.ticketFilter || {};
  const kaiziqResolveConnectionId = ticketFilter?.kaiziqResolveConnectionIdList?.[0];

  const getDayStartMs = (date) => {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy.getTime();
  };

  const getDayEndMs = (date) => {
    const copy = new Date(date);
    copy.setHours(23, 59, 59, 999);
    return copy.getTime();
  };

  const getDateRangeTimestamps = (rangeKey) => {
    const now = new Date();
    const todayStart = getDayStartMs(now);
    const todayEnd = getDayEndMs(now);

    if (rangeKey === 'all') return { createTimeStampFrom: 0, createTimeStampTo: 0 };
    if (rangeKey === 'last7') {
      const from = new Date(now);
      from.setDate(now.getDate() - 6);
      return { createTimeStampFrom: getDayStartMs(from), createTimeStampTo: todayEnd };
    }
    if (rangeKey === 'last30') {
      const from = new Date(now);
      from.setDate(now.getDate() - 29);
      return { createTimeStampFrom: getDayStartMs(from), createTimeStampTo: todayEnd };
    }
    if (rangeKey === 'last90') {
      const from = new Date(now);
      from.setDate(now.getDate() - 89);
      return { createTimeStampFrom: getDayStartMs(from), createTimeStampTo: todayEnd };
    }
    if (rangeKey === 'thisMonth') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      return { createTimeStampFrom: getDayStartMs(monthStart), createTimeStampTo: todayEnd };
    }
    if (rangeKey === 'lastMonth') {
      const monthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return { createTimeStampFrom: getDayStartMs(monthStart), createTimeStampTo: getDayEndMs(monthEnd) };
    }
    return { createTimeStampFrom: todayStart, createTimeStampTo: todayEnd };
  };

  const toDayStartTimestamp = (dateString) => {
    if (!dateString) return 0;
    const date = new Date(`${dateString}T00:00:00`);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };

  const toDayEndTimestamp = (dateString) => {
    if (!dateString) return 0;
    const date = new Date(`${dateString}T23:59:59.999`);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };

  const toDateInputValue = (timestamp) => {
    if (!timestamp || Number(timestamp) === 0) return '';
    const date = new Date(Number(timestamp));
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  };

  const getDateRangeKeyFromFilter = (createTimeStampFrom, createTimeStampTo) => {
    const from = Number(createTimeStampFrom || 0);
    const to = Number(createTimeStampTo || 0);
    const keys = ['all', 'last7', 'last30', 'last90', 'thisMonth', 'lastMonth'];
    const matched = keys.find((key) => {
      const range = getDateRangeTimestamps(key);
      return range.createTimeStampFrom === from && range.createTimeStampTo === to;
    });
    return matched || 'custom';
  };

  const updateTicketFilter = (partial) => {
    const next = {
      ...filters,
      ticketFilter: {
        ...ticketFilter,
        ...partial
      }
    };
    onFilterChange && onFilterChange(next);
  };

  const detectedDateRangeKey = getDateRangeKeyFromFilter(ticketFilter?.createTimeStampFrom, ticketFilter?.createTimeStampTo);
  const selectedDateRangeKey = dateRangeMode === 'custom' ? 'custom' : detectedDateRangeKey;
  const selectedSortValue = `${ticketFilter?.sortBy || 'createTimeStamp'}:${ticketFilter?.sortAsc ? 'asc' : 'desc'}`;
  const selectedAssigneeOptions = assigneeOptions.filter((user) => (ticketFilter?.assigneeIdList || []).includes(user.id));
  const selectedReporterOptions = (ticketFilter?.createdByEmailList || []).filter((email) => createdByEmailOptions.includes(email));
  const selectMenuProps = {
    PaperProps: {
      className: className + '__menuPaper'
    }
  };

  // Sync external filter changes (e.g., when clearing)
  useEffect(() => {
    setSearchQuery(filters?.ticketFilter?.searchQuery || '');
  }, [filters?.ticketFilter?.searchQuery]);

  useEffect(() => {
    getSimpleUsersByProjectIdRef.current = getSimpleUsersByProjectId;
  }, [getSimpleUsersByProjectId]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      if (!isTicketFilter || !showAdvancedFilters || !currentProjectId || !kaiziqResolveConnectionId) return;

      const requestKey = `${currentProjectId}:${kaiziqResolveConnectionId}`;
      if (lastLoadedFilterOptionsKeyRef.current === requestKey || isFilterOptionsRequestInFlightRef.current) return;

      isFilterOptionsRequestInFlightRef.current = true;
      setIsFilterOptionsLoading(true);
      try {
        const [allUsers, adminFieldListObj] = await Promise.all([
          getSimpleUsersByProjectIdRef.current(currentProjectId),
          fieldService.getListFiltered(
            {
              "fieldFilter" : { 
                projectIdList: [currentProjectId],
                tableType: 'kaiziqResolveConnection',
                tableId: kaiziqResolveConnectionId,
                filterAttribute1List: ["admin"]
              }
            }
          )
        ]);

        const normalizedUsers = (allUsers || [])
          .map((user) => ({
            id: Number(user.id),
            label: user.fullName || user.userName,
            userName: user.userName
          }))
          .filter((user) => !!user.id);
        setAssigneeOptions(normalizedUsers);

        const parsedFieldResponse = JSON.parse(adminFieldListObj?.body || '{}');
        const adminFieldList = parsedFieldResponse?.result || [];
        const statusField = adminFieldList.find((field) => field.title === 'status');
        const parsedStatusOptions = statusField?.options ? JSON.parse(statusField.options) : [];
        const normalizedStatusOptions = (parsedStatusOptions || [])
          .map((status) => ({
            id: Number(status.id),
            value: status.value,
            label: (status.value || '').charAt(0).toUpperCase() + (status.value || '').slice(1)
          }))
          .filter((status) => !!status.id);
        setStatusOptions(normalizedStatusOptions);

        const emailSet = new Set();
        try {
          let formUserListObj = await formUserAccessLinkService.getListByForm(kaiziqResolveConnectionId);
          formUserListObj = JSON.parse(formUserListObj?.body || '[]');
          formUserListObj.forEach((row) => {
            if (row?.createdByEmail) emailSet.add(row.createdByEmail);
          });
        } catch (e) {
          errorHandler.logError(e);
        }

        try {
          let ticketUserEmailCountObj = await ticketUserEmailCountViewService.getListByForm(kaiziqResolveConnectionId);
          ticketUserEmailCountObj = JSON.parse(ticketUserEmailCountObj?.body || '[]');
          ticketUserEmailCountObj.forEach((row) => {
            if (row?.createdByEmail) emailSet.add(row.createdByEmail);
          });
        } catch (e) {
          errorHandler.logError(e);
        }
        setCreatedByEmailOptions(Array.from(emailSet).sort((a, b) => a.localeCompare(b)));
        lastLoadedFilterOptionsKeyRef.current = requestKey;
      } catch (e) {
        errorHandler.handleError(e);
      }
      isFilterOptionsRequestInFlightRef.current = false;
      setIsFilterOptionsLoading(false);
    };

    loadFilterOptions();
  }, [
    isTicketFilter,
    showAdvancedFilters,
    currentProjectId,
    kaiziqResolveConnectionId,
    errorHandler,
    fieldService,
    formUserAccessLinkService,
    ticketUserEmailCountViewService
  ]);

  // Debounced propagation of search query (without triggering search fetch)
  useEffect(() => {
    const t = setTimeout(() => {
      if (isDirty) {
        try {
          const sanitized = DOMPurify.sanitize(searchQuery);
          const next = {
            ...filters,
            ticketFilter: {
              ...filters.ticketFilter,
              searchQuery: sanitized
            }
          };
          onFilterChange && onFilterChange(next);
        } catch (e) {
          errorHandler.handleError(e);
        }
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, isDirty, filters, onFilterChange, errorHandler]);

  const runSearch = useCallback(() => {
    handleSearch && handleSearch();
  }, [handleSearch]);

  const handleInputChange = (e) => {
    setIsDirty(true);
    setSearchQuery(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') runSearch();
  };

  const handleClear = () => {
    setSearchQuery('');
    setIsDirty(false);
    // Reset filters immutably
    if (onFilterChange && defaultFilters) onFilterChange(defaultFilters);
    clearFilters && clearFilters();
  };

  const handleButtonSearch = () => {
    // Ensure latest query propagated
    const sanitized = DOMPurify.sanitize(searchQuery);
    const next = {
      ...filters,
      ticketFilter: {
        ...filters.ticketFilter,
        searchQuery: sanitized
      }
    };
    onFilterChange && onFilterChange(next);
    runSearch();
  };

  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters((prev) => !prev);
  };

  const handleApplyFilters = () => {
    toggleAdvancedFilters();
    // Trigger search after applying filters
    setTimeout(() => {
      runSearch();
    }, 0);
  };

  const handleDateRangeChange = (event) => {
    const rangeKey = event.target.value;
    if (rangeKey === 'custom') {
      setDateRangeMode('custom');
      if (!ticketFilter?.createTimeStampFrom || !ticketFilter?.createTimeStampTo) {
        updateTicketFilter(getDateRangeTimestamps('last7'));
      }
      return;
    }
    setDateRangeMode('');
    updateTicketFilter(getDateRangeTimestamps(rangeKey));
  };

  const handleSortChange = (event) => {
    const [sortBy, direction] = String(event.target.value).split(':');
    updateTicketFilter({
      sortBy,
      sortAsc: direction === 'asc'
    });
  };

  const renderControlLoader = () => (
    isFilterOptionsLoading ? <CircularProgress size={12} className={className + '__advancedControlLoader'} /> : null
  );

  return (
    <div className={className}>
      <Box className={className + '__layout'}>
        <Box className={className + '__searchGroup'}>
          <TextField
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            size="small"
            fullWidth
            aria-label="Search tickets"
            className={className + '__searchField'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box className={className + '__searchAdornment'}>
                    {searchQuery ? (
                      <Tooltip title="Clear" enterDelay={400}>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={handleClear}
                          aria-label="Clear search"
                          className={className + '__clearIconBtn'}
                        >
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Search" enterDelay={300}>
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={handleButtonSearch}
                        aria-label="Search tickets"
                        className={className + '__searchDecorBtn'}
                      >
                        <SearchIcon className={className + '__searchIcon'} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Box className={className + '__actions'}>
          {isPhoneView ? (
            <>
              <Box className={className + '__mobileIconRow'}>
                <Tooltip title="Clear Filters" enterDelay={300}>
                  <IconButton
                    onClick={handleClear}
                    aria-label="Clear Filters"
                    className={className + '__actionIconButton'}
                  >
                    <ClearFiltersIcon />
                  </IconButton>
                </Tooltip>

                {isTicketFilter && (
                  <Box className={className + '__exportWrap'}>
                    <TicketExportJob
                      filters={filters}
                      statusList={statusList}
                      iconOnly={true}
                      iconButtonClassName={className + '__actionIconButton'}
                    />
                  </Box>
                )}

                {isTicketFilter && (
                  <Box className={className + '__mobileIconRow__spacer'} />
                )}

                {isTicketFilter && (
                  <button
                    type="button"
                    onClick={toggleAdvancedFilters}
                    className={className + '__advancedLink'}
                    aria-expanded={showAdvancedFilters}
                    aria-label="Toggle advanced filters"
                  >
                    {showAdvancedFilters ? 'Hide Advanced Filters' : 'Advanced Filters'}
                  </button>
                )}
              </Box>
            </>
          ) : (
            <>
              <BorderBlueButtonV2
                text="Clear Filters"
                onClick={handleClear}
                className={className + '__clearButton'}
              />
              {isTicketFilter && (
                <button
                  type="button"
                  onClick={toggleAdvancedFilters}
                  className={className + '__advancedLink'}
                  aria-expanded={showAdvancedFilters}
                  aria-label="Toggle advanced filters"
                >
                  {showAdvancedFilters ? 'Hide Advanced Filters' : 'Advanced Filters'}
                </button>
              )}
              <BorderBlueButtonV2
                text="Search"
                onClick={handleButtonSearch}
                className={className + '__searchButton'}
              />
              {isTicketFilter && (
                <Box className={className + '__exportWrap'}>
                  <TicketExportJob
                    filters={filters}
                    statusList={statusList}
                    exportButtonClassName={className + '__searchButton'}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {isTicketFilter && !isPhoneView && showAdvancedFilters && (
        <Box className={className + '__advancedRow'}>
          <FormControl size="small" className={className + '__advancedControl'}>
            {renderControlLoader()}
            <InputLabel id="advanced-status-list-label">Status</InputLabel>
            <Select
              labelId="advanced-status-list-label"
              multiple
              MenuProps={selectMenuProps}
              value={ticketFilter?.statusList || []}
              label="Status"
              onChange={(e) => updateTicketFilter({ statusList: e.target.value })}
              renderValue={(selected) => {
                const selectedStatuses = statusOptions.filter((status) => selected.includes(status.id));
                return selectedStatuses.length ? selectedStatuses.map((s) => s.label).join(', ') : 'All';
              }}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  <Checkbox checked={(ticketFilter?.statusList || []).includes(status.id)} size="small" />
                  <ListItemText primary={status.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <SearchableMultiFilterControl
            className={className}
            label="Assignees"
            options={assigneeOptions}
            selectedOptions={selectedAssigneeOptions}
            onSelectionChange={(selected) => updateTicketFilter({ assigneeIdList: selected.map((user) => user.id) })}
            getOptionLabel={(option) => option.label || option.userName || ''}
            getOptionValue={(option) => option.id}
            isLoading={isFilterOptionsLoading}
            renderLoader={renderControlLoader}
            noOptionsLabel="assignees"
          />

          <SearchableMultiFilterControl
            className={className}
            label="Reporter Emails"
            options={createdByEmailOptions}
            selectedOptions={selectedReporterOptions}
            onSelectionChange={(selected) => updateTicketFilter({ createdByEmailList: selected })}
            getOptionLabel={(option) => option || ''}
            getOptionValue={(option) => option}
            isLoading={isFilterOptionsLoading}
            renderLoader={renderControlLoader}
            noOptionsLabel="reporter emails"
          />

          <FormControl size="small" className={className + '__advancedControl'}>
            {renderControlLoader()}
            <InputLabel id="advanced-date-range-label">Date Range</InputLabel>
            <Select
              labelId="advanced-date-range-label"
              MenuProps={selectMenuProps}
              value={selectedDateRangeKey}
              label="Date Range"
              onChange={handleDateRangeChange}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="last7">Last 7 Days</MenuItem>
              <MenuItem value="last30">Last 30 Days</MenuItem>
              <MenuItem value="last90">Last 90 Days</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
              <MenuItem value="lastMonth">Last Month</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          {selectedDateRangeKey === 'custom' && (
            <Box className={className + '__advancedControl ' + className + '__advancedControl--dateRange'}>
              {renderControlLoader()}
              <TextField
                size="small"
                type="date"
                label="Start"
                InputLabelProps={{ shrink: true }}
                className={className + '__advancedCustomDate'}
                value={toDateInputValue(ticketFilter?.createTimeStampFrom)}
                onChange={(e) => {
                  setDateRangeMode('custom');
                  updateTicketFilter({ createTimeStampFrom: toDayStartTimestamp(e.target.value) });
                }}
              />
              <TextField
                size="small"
                type="date"
                label="End"
                InputLabelProps={{ shrink: true }}
                className={className + '__advancedCustomDate'}
                value={toDateInputValue(ticketFilter?.createTimeStampTo)}
                onChange={(e) => {
                  setDateRangeMode('custom');
                  updateTicketFilter({ createTimeStampTo: toDayEndTimestamp(e.target.value) });
                }}
              />
            </Box>
          )}

          <FormControl size="small" className={className + '__advancedControl'}>
            {renderControlLoader()}
            <InputLabel id="advanced-sort-label">Sort</InputLabel>
            <Select
              labelId="advanced-sort-label"
              MenuProps={selectMenuProps}
              value={selectedSortValue}
              label="Sort"
              onChange={handleSortChange}
            >
              <MenuItem value="createTimeStamp:desc">Created Time - Newest First</MenuItem>
              <MenuItem value="createTimeStamp:asc">Created Time - Oldest First</MenuItem>
              <MenuItem value="reportedAt:desc">Reported Date - Newest First</MenuItem>
              <MenuItem value="reportedAt:asc">Reported Date - Oldest First</MenuItem>
              <MenuItem value="ticketId:desc">Ticket Tag - Z to A</MenuItem>
              <MenuItem value="ticketId:asc">Ticket Tag - A to Z</MenuItem>
              <MenuItem value="priority:desc">Priority - High to Low</MenuItem>
              <MenuItem value="priority:asc">Priority - Low to High</MenuItem>
              <MenuItem value="status:asc">Status - A to Z</MenuItem>
              <MenuItem value="status:desc">Status - Z to A</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {isTicketFilter && isPhoneView && showAdvancedFilters && (
        <Drawer
          anchor="bottom"
          open={showAdvancedFilters}
          onClose={toggleAdvancedFilters}
          className={className + '__advancedDrawer'}
          PaperProps={{
            className: className + '__advancedDrawer__paper'
          }}
        >
          <Box className={className + '__advancedDrawer__content'}>
            <Box className={className + '__advancedDrawer__header'}>
              <h3 className={className + '__advancedDrawer__title'}>Filter Tickets</h3>
              <IconButton
                size="small"
                onClick={toggleAdvancedFilters}
                aria-label="Close filters"
                className={className + '__advancedDrawer__closeBtn'}
              >
                <ClearIcon />
              </IconButton>
            </Box>

            <Box className={className + '__advancedDrawer__body'}>
              <FormControl size="small" className={className + '__advancedControl'} fullWidth>
                {renderControlLoader()}
                <InputLabel id="advanced-status-list-label-mobile">Status</InputLabel>
                <Select
                  labelId="advanced-status-list-label-mobile"
                  multiple
                  MenuProps={selectMenuProps}
                  value={ticketFilter?.statusList || []}
                  label="Status"
                  onChange={(e) => updateTicketFilter({ statusList: e.target.value })}
                  renderValue={(selected) => {
                    const selectedStatuses = statusOptions.filter((status) => selected.includes(status.id));
                    return selectedStatuses.length ? selectedStatuses.map((s) => s.label).join(', ') : 'All';
                  }}
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Checkbox checked={(ticketFilter?.statusList || []).includes(status.id)} size="small" />
                      <ListItemText primary={status.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <SearchableMultiFilterControl
                className={className}
                label="Assignees"
                options={assigneeOptions}
                selectedOptions={selectedAssigneeOptions}
                onSelectionChange={(selected) => updateTicketFilter({ assigneeIdList: selected.map((user) => user.id) })}
                getOptionLabel={(option) => option.label || option.userName || ''}
                getOptionValue={(option) => option.id}
                isLoading={isFilterOptionsLoading}
                renderLoader={renderControlLoader}
                noOptionsLabel="assignees"
              />

              <SearchableMultiFilterControl
                className={className}
                label="Reporter Emails"
                options={createdByEmailOptions}
                selectedOptions={selectedReporterOptions}
                onSelectionChange={(selected) => updateTicketFilter({ createdByEmailList: selected })}
                getOptionLabel={(option) => option || ''}
                getOptionValue={(option) => option}
                isLoading={isFilterOptionsLoading}
                renderLoader={renderControlLoader}
                noOptionsLabel="reporter emails"
              />

              <FormControl size="small" className={className + '__advancedControl'} fullWidth>
                {renderControlLoader()}
                <InputLabel id="advanced-date-range-label-mobile">Date Range</InputLabel>
                <Select
                  labelId="advanced-date-range-label-mobile"
                  MenuProps={selectMenuProps}
                  value={selectedDateRangeKey}
                  label="Date Range"
                  onChange={handleDateRangeChange}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="last7">Last 7 Days</MenuItem>
                  <MenuItem value="last30">Last 30 Days</MenuItem>
                  <MenuItem value="last90">Last 90 Days</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>

              {selectedDateRangeKey === 'custom' && (
                <Box className={className + '__advancedControl ' + className + '__advancedControl--dateRange'}>
                  {renderControlLoader()}
                  <TextField
                    size="small"
                    type="date"
                    label="Start"
                    InputLabelProps={{ shrink: true }}
                    className={className + '__advancedCustomDate'}
                    value={toDateInputValue(ticketFilter?.createTimeStampFrom)}
                    onChange={(e) => {
                      setDateRangeMode('custom');
                      updateTicketFilter({ createTimeStampFrom: toDayStartTimestamp(e.target.value) });
                    }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="End"
                    InputLabelProps={{ shrink: true }}
                    className={className + '__advancedCustomDate'}
                    value={toDateInputValue(ticketFilter?.createTimeStampTo)}
                    onChange={(e) => {
                      setDateRangeMode('custom');
                      updateTicketFilter({ createTimeStampTo: toDayEndTimestamp(e.target.value) });
                    }}
                    fullWidth
                  />
                </Box>
              )}

              <FormControl size="small" className={className + '__advancedControl'} fullWidth>
                {renderControlLoader()}
                <InputLabel id="advanced-sort-label-mobile">Sort</InputLabel>
                <Select
                  labelId="advanced-sort-label-mobile"
                  MenuProps={selectMenuProps}
                  value={selectedSortValue}
                  label="Sort"
                  onChange={handleSortChange}
                >
                  <MenuItem value="createTimeStamp:desc">Created Time - Newest First</MenuItem>
                  <MenuItem value="createTimeStamp:asc">Created Time - Oldest First</MenuItem>
                  <MenuItem value="reportedAt:desc">Reported Date - Newest First</MenuItem>
                  <MenuItem value="reportedAt:asc">Reported Date - Oldest First</MenuItem>
                  <MenuItem value="ticketId:desc">Ticket Tag - Z to A</MenuItem>
                  <MenuItem value="ticketId:asc">Ticket Tag - A to Z</MenuItem>
                  <MenuItem value="priority:desc">Priority - High to Low</MenuItem>
                  <MenuItem value="priority:asc">Priority - Low to High</MenuItem>
                  <MenuItem value="status:asc">Status - A to Z</MenuItem>
                  <MenuItem value="status:desc">Status - Z to A</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box className={className + '__advancedDrawer__footer'}>
              <BorderBlueButtonV2
                text="Clear Filters"
                onClick={handleClear}
                className={className + '__advancedDrawer__clearBtn'}
              />
              <BorderBlueButtonV2
                text="Apply Filters"
                onClick={handleApplyFilters}
                className={className + '__advancedDrawer__applyBtn'}
              />
            </Box>
          </Box>
        </Drawer>
      )}
    </div>
  );
}
